'use client'

import { IconInfoCircle } from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'
import { useEffect, useRef, useState } from 'react'

import {
  buildPlaceIntroductionText,
  formatPlaceIntroductionDate,
} from '@/features/seoPlaceDetail/lib'
import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import {
  type IPlaceIntroduction,
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { IconAiSparkle, IconProfileFill } from '@/shared/assets/icons'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { cn } from '@/shared/lib/cn'
import { OptimizedImage } from '@/shared/ui'

const HERO_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.HERO,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.HERO,
}

const MECHANIC_DISPLAY_COUNT = 3

interface IPlaceBasicInfoProps {
  place: ISeoPlace
  productGroups: IProductGroup[]
  introduction: IPlaceIntroduction | null
}

const PlaceBasicInfo = ({
  place,
  productGroups,
  introduction,
}: IPlaceBasicInfoProps) => {
  const reviewCount = place.statistic?.reviewCount ?? 0
  const diaryCount = place.statistic?.diaryCount ?? 0

  const mechanics = (place.mechanicInfo?.mechanics ?? []).slice(
    0,
    MECHANIC_DISPLAY_COUNT
  )
  const representative = mechanics[0]
  const representativeCareerYears = representative
    ? calculateCareerYears(representative.career)
    : 0

  const introductionText = introduction
    ? buildPlaceIntroductionText({ introduction, place, productGroups })
    : null
  const sectionRef = useRef<HTMLElement | null>(null)
  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: HERO_IMPRESSION_PARAMS,
  })

  return (
    <section
      ref={sectionRef}
      aria-label='정비소 정보'
      className='flex flex-col gap-dimension-x4 bg-background-surface-default px-dimension-x5 py-dimension-x6'
    >
      <div className='flex flex-col items-start'>
        <Text
          as='h1'
          variant='heading-3-semibold'
          color='text-foreground-neutral-strong'
        >
          {place.placeName}
        </Text>
        <div className='flex items-center gap-dimension-x1'>
          <button
            type='button'
            onClick={() => scrollToSection('place-performance-section')}
            className='-mx-0.5 flex cursor-pointer items-center pl-dimension-x1 pr-dimension-x1-5 py-dimension-x1'
          >
            <Text
              variant='body-1-semibold'
              color='text-foreground-neutral-alternative'
              className='underline underline-offset-2'
            >
              시공 {diaryCount.toLocaleString()}
            </Text>
          </button>
          <span
            aria-hidden='true'
            className='size-[2px] rounded-full bg-foreground-neutral-alternative'
          />
          <button
            type='button'
            onClick={() => scrollToSection('place-review-section')}
            className='-mx-0.5 flex cursor-pointer items-center pl-dimension-x1 pr-dimension-x1-5 py-dimension-x1'
          >
            <Text
              variant='body-1-semibold'
              color='text-foreground-neutral-alternative'
              className='underline underline-offset-2'
            >
              리뷰 {reviewCount.toLocaleString()}
            </Text>
          </button>
        </div>
      </div>

      {introductionText && introduction && (
        <AiIntroductionCard
          text={introductionText}
          footer={formatPlaceIntroductionDate()}
        />
      )}

      {representative && (
        <div className='flex items-center gap-dimension-x3'>
          <div className='relative isolate flex h-12 items-center pr-dimension-x5'>
            {mechanics.map((mechanic, index) => (
              <div
                key={`${mechanic.name}-${index}`}
                className={cn(
                  'relative -mr-dimension-x5 size-[52px] shrink-0 overflow-hidden rounded-full border-2',
                  index === 0
                    ? 'border-foreground-neutral-inverse bg-background-surface-basement'
                    : 'border-background-surface-default bg-background-surface-default'
                )}
                style={{ zIndex: mechanics.length - index }}
              >
                {mechanic.imageUrl ? (
                  <OptimizedImage
                    src={mechanic.imageUrl}
                    alt={mechanic.name}
                    width='100%'
                    height='100%'
                    radius={0}
                    sizes='48px'
                  />
                ) : (
                  <div className='flex size-full items-center justify-center'>
                    <IconProfileFill
                      fill='var(--color-foreground-neutral-muted)'
                      width='24'
                      height='24'
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className='flex min-w-px flex-1 flex-col items-start justify-center pt-0.5'>
            <Text
              variant='body-1-semibold'
              color='text-foreground-neutral-strong'
            >
              대표 정비사 : {representative.name}님
            </Text>
            {representativeCareerYears > 0 && (
              <Text
                variant='body-2-medium'
                color='text-foreground-neutral-assistive'
              >
                자동차 정비 경력 {representativeCareerYears}년
              </Text>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

interface IAiIntroductionCardProps {
  text: string
  footer: string
}

const AiIntroductionCard = ({ text, footer }: IAiIntroductionCardProps) => (
  <div className='flex flex-col gap-dimension-x2 rounded-dimension-x4 border border-foreground-neutral-muted bg-background-surface-default px-[16px] py-dimension-x3'>
    <AiIntroductionHeader />
    <Text
      variant='body-1-medium'
      color='text-foreground-neutral-strong'
      className='whitespace-pre-wrap'
    >
      {text}
    </Text>
    {footer && (
      <Text variant='body-2-medium' color='text-foreground-neutral-alternative'>
        {footer}
      </Text>
    )}
  </div>
)

const scrollToSection = (id: string) => {
  if (typeof document === 'undefined') return
  const target = document.getElementById(id)
  if (!target) return
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// career 필드는 API에서 입사 연도(예: 2020) 또는 경력 년수로 올 수 있음.
// 1900~현재년도 범위면 입사 연도로 간주해 현재까지 년수로 변환 (마이클 앱과 동일하게 +1).
const calculateCareerYears = (career: number): number => {
  if (!Number.isFinite(career) || career <= 0) return 0
  const currentYear = new Date().getFullYear()
  if (career >= 1900 && career <= currentYear) {
    return Math.max(0, currentYear - career + 1)
  }
  return career
}

const AI_INTRODUCTION_TOOLTIP_TEXT =
  '이 정비소를 방문한 마이클 실거래\n데이터를 바탕으로 요약한 내용입니다.'

const AiIntroductionHeader = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [tooltipLeft, setTooltipLeft] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const infoAnchorRef = useRef<HTMLSpanElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const tooltipId = 'place-ai-introduction-tooltip'

  // 클릭 시점에 anchor 위치를 직접 측정 — useLayoutEffect SSR 경고 회피
  const handleToggle = () => {
    setIsTooltipOpen(prev => {
      if (prev) return false
      const container = containerRef.current
      const anchor = infoAnchorRef.current
      if (container && anchor) {
        const containerRect = container.getBoundingClientRect()
        const anchorRect = anchor.getBoundingClientRect()
        setTooltipLeft(
          anchorRect.left - containerRect.left + anchorRect.width / 2
        )
      }
      return true
    })
  }

  useEffect(() => {
    if (!isTooltipOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (triggerRef.current?.contains(target)) return
      if (tooltipRef.current?.contains(target)) return
      setIsTooltipOpen(false)
    }
    const handleScroll = () => setIsTooltipOpen(false)

    window.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isTooltipOpen])

  return (
    <div ref={containerRef} className='relative self-start'>
      <button
        ref={triggerRef}
        type='button'
        aria-label='마이클 AI 요약 설명 보기'
        aria-expanded={isTooltipOpen}
        aria-controls={tooltipId}
        onClick={handleToggle}
        className='flex h-[24px] items-center gap-dimension-x1'
      >
        <IconAiSparkle width={24} height={24} />
        <Text variant='title-2-semibold' color='text-foreground-brand-strong'>
          마이클 실거래 분석
        </Text>
        <span ref={infoAnchorRef} className='inline-flex'>
          <IconInfoCircle
            width={18}
            height={18}
            className='text-foreground-neutral-muted'
          />
        </span>
      </button>

      {isTooltipOpen && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          role='tooltip'
          style={{ left: `${tooltipLeft}px` }}
          className='absolute bottom-[calc(100%-8px)] z-10 w-max max-w-[280px] -translate-x-1/2 rounded-dimension-x1 bg-background-neutral-inverse px-dimension-x3 py-dimension-x2'
        >
          <Text
            variant='body-2-regular'
            color='text-foreground-neutral-inverse'
            className='whitespace-pre-line'
          >
            {AI_INTRODUCTION_TOOLTIP_TEXT}
          </Text>
          <svg
            aria-hidden
            width='8'
            height='4'
            viewBox='0 0 8 4'
            className='absolute -bottom-[3px] left-1/2 -translate-x-1/2'
            style={{ fill: 'var(--color-background-neutral-inverse)' }}
          >
            <path d='M0 0 L4 4 L8 0 Z' />
          </svg>
        </div>
      )}
    </div>
  )
}

export default PlaceBasicInfo
